import { z } from 'zod';
import { addCalendarDays, weekdayOf } from './absence.js';
import { CalendarDate, TaskPriority } from './project.js';
import { ChecklistItemsInput } from './task-checklist.js';
/**
 * Việc định kỳ của dự án bảo trì — APM-REC, người dùng chốt 2026-09-25.
 *
 * Một **mẫu** + một **lịch lặp**. Mỗi lần đến hạn, job sinh ra **một task
 * thật** (vào board, timesheet, velocity như mọi task) — không mở lại task cũ:
 * mở lại là mất lịch sử từng lần, và Replay không phân biệt được lần rà soát
 * tháng 8 với tháng 9.
 */
/**
 * - `daily`: mỗi **ngày làm việc** (ngày nghỉ bị bỏ qua, không dời — dời thì
 *   thứ Bảy và Chủ nhật cùng dồn vào thứ Hai);
 * - `weekly`: những thứ trong tuần đã chọn (`weekdays`);
 * - `monthly`: ngày `monthDay` của tháng, `0` = ngày cuối tháng; tháng ngắn hơn
 *   thì rơi về ngày cuối (31 ⇒ 30/4, 28 hay 29/2);
 * - `interval`: mỗi `intervalDays` ngày kể từ `startDate`.
 */
export const RecurrenceFreq = z.enum(['daily', 'weekly', 'monthly', 'interval']);
export const RecurrenceRule = z
    .object({
    freq: RecurrenceFreq,
    /** `0` = Chủ nhật … `6` = thứ Bảy, đúng `weekdayOf`. Chỉ cho `weekly`. */
    weekdays: z.array(z.number().int().min(0).max(6)).max(7).default([]),
    /** `1`–`31`, hoặc `0` = ngày cuối tháng. Chỉ cho `monthly`. */
    monthDay: z.number().int().min(0).max(31).nullable().default(null),
    /** Chỉ cho `interval`. */
    intervalDays: z.number().int().min(2).max(366).nullable().default(null),
    startDate: CalendarDate,
    endDate: CalendarDate.nullable().default(null),
})
    .superRefine((rule, ctx) => {
    if (rule.freq === 'weekly' && rule.weekdays.length === 0) {
        ctx.addIssue({
            code: 'custom',
            path: ['weekdays'],
            message: 'Chọn ít nhất một thứ trong tuần',
        });
    }
    if (rule.freq === 'monthly' && rule.monthDay === null) {
        ctx.addIssue({ code: 'custom', path: ['monthDay'], message: 'Chọn ngày trong tháng' });
    }
    if (rule.freq === 'interval' && rule.intervalDays === null) {
        ctx.addIssue({
            code: 'custom',
            path: ['intervalDays'],
            message: 'Nhập số ngày giữa hai lần',
        });
    }
    if (rule.endDate !== null && rule.endDate < rule.startDate) {
        ctx.addIssue({
            code: 'custom',
            path: ['endDate'],
            message: 'Ngày kết thúc trước ngày bắt đầu',
        });
    }
});
/**
 * Những ngày **theo lịch** (chưa dời ngày nghỉ) của một quy tắc trong
 * `[from, to]`, tăng dần. Hàm thuần: server dùng để sinh task, web dùng để xem
 * trước "3 lần tới" ngay trong form.
 *
 * Dời ngày nghỉ **không** nằm ở đây: nó cần lịch lễ của tổ chức, thứ web không
 * có trong form. Xem `occurrenceDueDate`.
 */
export function scheduledDates(rule, from, to) {
    const start = from > rule.startDate ? from : rule.startDate;
    const end = rule.endDate !== null && rule.endDate < to ? rule.endDate : to;
    if (end < start)
        return [];
    const dates = [];
    // Trần an toàn: một năm mỗi ngày. Cửa sổ thật chỉ vài ngày tới vài tuần.
    const limit = 400;
    if (rule.freq === 'interval') {
        const step = rule.intervalDays ?? 1;
        const gap = daysBetween(rule.startDate, start);
        let cursor = addCalendarDays(rule.startDate, Math.ceil(gap / step) * step);
        while (cursor <= end && dates.length < limit) {
            dates.push(cursor);
            cursor = addCalendarDays(cursor, step);
        }
        return dates;
    }
    for (let cursor = start; cursor <= end && dates.length < limit; cursor = addCalendarDays(cursor, 1)) {
        if (rule.freq === 'daily')
            dates.push(cursor);
        else if (rule.freq === 'weekly') {
            if (rule.weekdays.includes(weekdayOf(cursor)))
                dates.push(cursor);
        }
        else if (cursor === monthlyDateOf(cursor, rule.monthDay ?? 0))
            dates.push(cursor);
    }
    return dates;
}
/**
 * Hạn của một lần: ngày theo lịch, dời sang ngày làm việc kế tiếp nếu rơi vào
 * ngày nghỉ. `null` = bỏ lần này (chỉ `daily` gặp ngày nghỉ).
 */
export function occurrenceDueDate(freq, scheduled, isWorkingDay) {
    if (isWorkingDay(scheduled))
        return scheduled;
    if (freq === 'daily')
        return null;
    let cursor = scheduled;
    // Trần 60 ngày: một chuỗi nghỉ dài bất thường không được treo vòng lặp.
    for (let step = 0; step < 60; step += 1) {
        cursor = addCalendarDays(cursor, 1);
        if (isWorkingDay(cursor))
            return cursor;
    }
    return scheduled;
}
/** Ngày của tháng chứa `date` ứng với `monthDay` (`0` = cuối tháng; kẹp về cuối tháng). */
function monthlyDateOf(date, monthDay) {
    const [year, month] = date.split('-').map(Number);
    const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const day = monthDay === 0 ? last : Math.min(monthDay, last);
    return `${date.slice(0, 7)}-${String(day).padStart(2, '0')}`;
}
function daysBetween(from, to) {
    return Math.round((new Date(`${to}T00:00:00Z`).getTime() - new Date(`${from}T00:00:00Z`).getTime()) / 86_400_000);
}
/** Tạo / sửa một mẫu. Lịch và nội dung đi cùng nhau: sửa mẫu chỉ ảnh hưởng các lần **sau**. */
export const RecurrenceInput = z.object({
    title: z.string().trim().min(1).max(500),
    description: z.string().max(20_000).nullable().default(null),
    taskTypeId: z.uuid(),
    assigneeId: z.uuid().nullable().default(null),
    priority: TaskPriority.default('medium'),
    estimateHours: z.number().positive().max(10_000).nullable().default(null),
    checklist: ChecklistItemsInput.default([]),
    rule: RecurrenceRule,
    /** Hiện trước bao nhiêu ngày so với hạn. `0` = sinh đúng ngày đến hạn. */
    leadDays: z.number().int().min(0).max(60).default(0),
});
export const SetRecurrenceActiveInput = z.object({ isActive: z.boolean() });
/** Một lần đã sinh — là một task. */
export const RecurrenceOccurrence = z.object({
    taskId: z.uuid(),
    key: z.string(),
    occurrenceDate: CalendarDate,
    dueDate: CalendarDate.nullable(),
    statusName: z.string(),
    done: z.boolean(),
    assigneeName: z.string().nullable(),
});
export const RecurrenceView = z.object({
    id: z.uuid(),
    projectId: z.uuid(),
    title: z.string(),
    description: z.string().nullable(),
    taskTypeId: z.uuid(),
    taskTypeName: z.string(),
    assigneeId: z.uuid().nullable(),
    assigneeName: z.string().nullable(),
    priority: TaskPriority,
    estimateHours: z.number().nullable(),
    checklist: ChecklistItemsInput,
    rule: RecurrenceRule,
    leadDays: z.number().int(),
    isActive: z.boolean(),
    /** Ngày theo lịch của lần kế tiếp **chưa sinh**; `null` khi đã hết lịch hoặc đang tạm dừng. */
    nextDate: CalendarDate.nullable(),
    /** Số lần đã sinh mà chưa xong — khác 0 thì giao diện hiện đỏ. */
    pendingCount: z.number().int().nonnegative(),
    /** Mới nhất trước, tối đa 20 lần. */
    occurrences: z.array(RecurrenceOccurrence),
});
//# sourceMappingURL=recurrence.js.map